import { PORTAL_MEDIA_LIMITS, type PortalViewOutput } from '@garage/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useUploads } from '../../garage/components/photo-picker/useUploads.js';
import { AttachDropArea, MessageComposer } from '../../garage/components/composer/MessageComposer.js';
import { messageBody, useAttachments } from '../../garage/components/composer/useAttachments.js';
import { formatDateTime } from '../../garage/lib/format.js';
import { api } from '../../shared/api.js';
import { MessageBubble } from '../../shared/components/MessageBubble.js';
import { tone } from '../../shared/components/tokens.js';
import { PORTAL_QUERY_KEY } from '../keys.js';
import { useTz } from '../timezone.js';
import { PortalEventCard } from './PortalEventCard.js';
import { MessagePhotoGrid } from './MessagePhotoGrid.js';
import { uploadPortalPhoto } from '../upload.js';
import { ofName } from '../../garage/lib/format.js';


/**
 * Échanges avec le garage. Une question n'est jamais une décision : pour accepter ou refuser un
 * complément, le client utilise les boutons prévus. Vague 4 : photos jointes dans les deux sens,
 * liens web sûrs (domaine affiché), bulles « vous » et « garage » de couleurs distinctes.
 * Saisie (02/10/2026) : `MessageComposer` (trombone : galerie ou appareil photo), photos déposables.
 */
export function PortalQuestions({
  messages,
  garageName,
}: {
  messages: PortalViewOutput['messages'];
  garageName: string;
}) {
  const qc = useQueryClient();
  const tz = useTz();
  const [body, setBody] = useState('');
  const attachments = useAttachments({ max: PORTAL_MEDIA_LIMITS.perMessage, maxBytes: PORTAL_MEDIA_LIMITS.maxBytes });
  const [sent, setSent] = useState(false);
  const uploads = useUploads(uploadPortalPhoto);
  const ask = useMutation({
    mutationFn: async () => {
      const { ids, failed } = await uploads.run(attachments.files);
      if (failed.length > 0) throw new Error('photos');
      return api('portal.ask', { body: { body: messageBody(body, ids.length), ...(ids.length ? { mediaIds: ids } : {}) } });
    },
    onSuccess: (view) => {
      qc.setQueryData(PORTAL_QUERY_KEY, view);
      setBody('');
      attachments.clear();
      uploads.reset();
      setSent(true);
    },
  });
  const list = [...messages].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const photoError = ask.error instanceof Error && ask.error.message === 'photos';

  return (
    <AttachDropArea attachments={attachments} className="space-y-3">
      {list.length > 0 ? (
        <ul className="space-y-3">
          {list.map((m) => {
            // Vague 6 : événement du dossier, carte mise en avant (jamais une bulle).
            if (m.kind === 'event' && m.event) return <PortalEventCard key={m.id} message={m} />;
            const mine = m.authorType === 'customer';
            return (
              <MessageBubble key={m.id} author={mine ? 'customer' : 'garage'} mine={mine} body={m.body} meta={`${mine ? 'Vous' : garageName} · ${formatDateTime(m.createdAt, tz)}`}>
                {m.complement ? (
                  <p className="mt-2 text-xs font-medium opacity-90">
                    {mine ? 'Demande de modification' : 'À propos'} des travaux « {m.complement.title} »
                  </p>
                ) : null}
                <MessagePhotoGrid
                  label="Photos jointes"
                  className="mt-2 grid grid-cols-3 gap-1.5"
                  photos={m.media.map((p) => ({ id: p.id, alt: p.uploadedBy === 'customer' ? 'Votre photo' : `Photo ${ofName(garageName)}` }))}
                />
              </MessageBubble>
            );
          })}
        </ul>
      ) : null}
      <MessageComposer
        label="Poser une question au garage"
        placeholder="Écrire au garage…"
        value={body}
        onValueChange={(v) => {
          setBody(v);
          setSent(false);
        }}
        maxLength={2000}
        onSubmit={() => {
          if (!ask.isPending) ask.mutate();
        }}
        busy={ask.isPending}
        sendLabel="Envoyer ma question"
        attachments={attachments}
        states={uploads.states}
        attachLabel="Photos à joindre à votre question"
        hint="Réponse par e-mail. Pour accepter ou refuser des travaux, utilisez l’onglet Travaux."
        feedback={
          sent ? (
            <p role="status" className="rounded-xl border border-success-line bg-success-bg p-3.5 text-success-fg">
              Votre question a été envoyée.
            </p>
          ) : ask.error ? (
            <p role="alert" className={`rounded-xl border p-3 ${tone.dangerBox}`}>
              {photoError
                ? 'Certaines photos n’ont pas pu être envoyées (voir ci-dessus). Retirez-les ou réessayez.'
                : 'Votre question n’a pas pu être envoyée. Vérifiez votre connexion et réessayez.'}
            </p>
          ) : null
        }
      />
    </AttachDropArea>
  );
}
